import React, { useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from '../../i18n/useTranslation';
import { flags } from '../../constants/assets';

export function LanguageSwitcher() {
  const { switchLanguage, currentLanguage, languageSwitcherTranslations } = useTranslation();
  const [open, setOpen] = useState(false);

  const handleLanguageChange = async (language: 'en' | 'ko') => {
    if (language && language !== currentLanguage) {
      await switchLanguage(language);
    }
    setOpen(false);
  };

  const languages = [
    { code: 'en' as const, label: 'English', country: 'United States' },
    { code: 'ko' as const, label: '한국어', country: 'South Korea' },
  ];

  return (
    <>
      <button
        type="button"
        aria-label="Toggle language switcher"
        aria-expanded={open}
        className="border-image-power flex h-[42px] items-center justify-center gap-2 rounded-md bg-power-button-gradient px-3 transition-all duration-200 md:hover:brightness-125 xl:w-[87px] xl:px-0"
        onClick={() => setOpen(true)}
      >
        <img src={currentLanguage === 'en' ? flags.enFlag : flags.koFlag} alt="en flag" />
        <p className="mt-0.5 font-poppins text-[22.5px] font-black lg:text-[12.5px] xl:text-[17.5px]">{currentLanguage === 'en' ? 'EN' : 'KO'}</p>
      </button>

      {open && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-shell/20 backdrop-blur-md">
          <div className="relative size-full shadow-2xl bg-checkout-card-gradient md:h-auto md:max-w-[400px] md:rounded-xl md:p-8">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute right-2 top-2 size-8 text-3xl font-bold leading-none text-[#ECEAED] transition-colors duration-200 hover:text-neon_yellow"
            >
              ×
            </button>

            <div className="mt-16 flex flex-col items-center justify-center overflow-hidden tracking-normal md:mt-0 md:h-auto">
              <svg
                className="mb-2 inline-block size-10 align-middle text-white"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12.87 15.07l-2.54-2.51.03-.03c1.74-1.94 2.98-4.17 3.71-6.53H17V4h-7V2H8v2H1v1.99h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z" />
              </svg>

              <div className="font-skolar mb-1 text-xl font-bold normal-case text-white">
                {languageSwitcherTranslations.title}
              </div>
              <div className="mb-6 max-w-[300px] text-center font-poppins text-sm font-light normal-case text-[#9ca3af] md:text-xs">
                {languageSwitcherTranslations.description}
              </div>

              <div className="flex flex-row gap-8">
                {languages.map((lang) => (
                  <button
                    type="button"
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    className={clsx(
                      'flex flex-col items-start rounded border px-4 py-2 text-base font-semibold transition-all duration-200',
                      {
                        'border-[#B978FF] bg-[#B978FF]/10 text-[#B978FF] shadow-lg': currentLanguage === lang.code,
                        'border-[#ECEAED33] bg-[#0E122C] text-[#ECEAED] hover:border-[#B978FF] hover:bg-[#B978FF]/10': currentLanguage !== lang.code,
                      },
                    )}
                  >
                    <div className="text-sm">
                      {lang.label}
                    </div>
                    <div className="text-xs font-light">
                      {lang.country}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default LanguageSwitcher;
